import {
  Fragment,
  memo,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import isEqual from 'lodash-es/isEqual'

import { EMPTY_OBJECT } from '../../../constants/common'
import { useResizeObserver, useThrottleFunc } from '../../../hooks'
import {
  TABLE_COLUMN_RESIZE_CONFIG,
  TABLE_ROW_CHECKBOX_WIDTH,
} from '../Table.constants'
import {
  useTableArrowNavigation,
  useTableEdgesIntersection,
  useTableSideScroll,
} from '../Table.hooks'
import { getTableRowKey } from '../Table.utils'
import { TableHeaderRow } from '../TableHeaderRow'
import { TableScrollButtonOverlay } from '../TableScrollButtonOverlay'

import * as styles from './TableComponent.module.css'

function TableComponent({
  TableBody = Fragment,
  TableRow,
  actionsColumnWidth = TABLE_ROW_CHECKBOX_WIDTH,
  arrowNavigationConfig: {
    disabled: arrowNavigationConfigDisabled = true,
    focusedRow: arrowNavigationExternalFocusedRow,
    ...arrowNavigationConfig
  } = EMPTY_OBJECT,
  bodyProps,
  checkIsItemSelected,
  className = styles.Table,
  columns,
  containerRef,
  context,
  getRowKey = getTableRowKey,
  headerRowProps: {
    className: headerRowClassName = styles.row,
    ...headerRowProps
  } = EMPTY_OBJECT,
  isLoading,
  items,
  rowProps,
  scrollWrapperClassName = styles.tableScrollWrapper,
  tableRowClassName = styles.row,
  visibleColumns,
  onChangeFocusedRow,
  onChangeSideScrollButtonY,
}) {
  const scrollRef = useRef(null)
  const tableRef = useRef(null)

  const prevColumnsRef = useRef(null)

  const [actionLeft, setActionLeft] = useState(null)
  const [emptyColumnSize, setEmptyColumnSize] = useState(0)
  const [focusedRow, setFocusedRow] = useState(null)

  const arrowNavFocusedRow = arrowNavigationExternalFocusedRow ?? focusedRow

  const [isResizing, setResizing] = useState(false)
  const [showCellBorder, setShowCellBorder] = useState(false)
  const [resizingColumn, setResizingColumn] = useState(null)

  const { handleScrollLeft, handleScrollRight } = useTableSideScroll({
    containerRef,
    scrollRef,
    tableRef,
    onChangeSideScrollButtonY,
  })

  function handleChangeFocusedRow(nextFocusedRow) {
    setFocusedRow(nextFocusedRow)

    onChangeFocusedRow?.(nextFocusedRow)
  }

  const { getItemProps: getArrowNavigationItemProps } = useTableArrowNavigation(
    {
      ...arrowNavigationConfig,
      containerRef: tableRef,
      disabled: arrowNavigationConfigDisabled,
      focusedRow: arrowNavFocusedRow,
      items,
      onActiveIndexChange: handleChangeFocusedRow,
    }
  )

  const {
    isIntersectingLeftEnd,
    isIntersectingRightEnd,
    leftEndIntersectionRef,
    rightEndIntersectionRef,
  } = useTableEdgesIntersection()

  function handleCalculateEmptyColumnSize() {
    if (scrollRef.current && tableRef.current) {
      setEmptyColumnSize(prevEmptyColumnSize =>
        Math.max(
          scrollRef.current.getBoundingClientRect().width -
            (tableRef.current.getBoundingClientRect().width -
              prevEmptyColumnSize),
          0
        )
      )
    }
  }

  function handleResize(element) {
    setActionLeft(element.offsetWidth + element.scrollLeft)

    handleCalculateEmptyColumnSize()
  }

  useLayoutEffect(() => {
    if (!isEqual(prevColumnsRef.current, columns)) {
      prevColumnsRef.current = columns

      handleCalculateEmptyColumnSize()
    }
  }, [columns])

  useEffect(() => {
    if (scrollRef.current) {
      setActionLeft(
        scrollRef.current.offsetWidth + scrollRef.current.scrollLeft
      )
    }
  }, [])

  function updateActionLeft() {
    setActionLeft(scrollRef.current.offsetWidth + scrollRef.current.scrollLeft)
  }

  const { throttledHandler: handleScroll } = useThrottleFunc(
    updateActionLeft,
    300
  )

  useResizeObserver({ callback: handleResize, ref: scrollRef })

  function renderRow(item, index) {
    const isSelected = checkIsItemSelected?.(item)

    return (
      <TableRow
        {...rowProps}
        actionLeft={actionLeft}
        actionsColumnWidth={actionsColumnWidth}
        className={tableRowClassName}
        columns={visibleColumns}
        context={context}
        emptyColumnSize={emptyColumnSize}
        focusedRow={focusedRow}
        getArrowNavigationItemProps={getArrowNavigationItemProps}
        index={index}
        isResizing={isResizing}
        isSelected={isSelected}
        item={item}
        key={getRowKey(item)}
        resizingColumn={resizingColumn}
        scrollRef={scrollRef}
        showCellBorder={showCellBorder}
        onSetFocusedRow={setFocusedRow}
      />
    )
  }

  return (
    <>
      <TableScrollButtonOverlay
        isIntersectingLeftEnd={isIntersectingLeftEnd}
        isIntersectingRightEnd={isIntersectingRightEnd}
        isLoading={isLoading}
        onScrollLeft={handleScrollLeft}
        onScrollRight={handleScrollRight}
      />
      <div
        className={scrollWrapperClassName}
        ref={scrollRef}
        onScroll={handleScroll}
      >
        <div className={className} ref={tableRef}>
          <div
            className={styles.leftEndIntersectionArea}
            ref={leftEndIntersectionRef}
          />
          <div
            className={styles.rightEndIntersectionArea}
            ref={rightEndIntersectionRef}
          />
          <TableHeaderRow
            {...headerRowProps}
            actionsColumnWidth={actionsColumnWidth}
            className={headerRowClassName}
            columnResizeConfig={
              headerRowProps?.columnResizeConfig || TABLE_COLUMN_RESIZE_CONFIG
            }
            columns={visibleColumns}
            columnsConfig={columns}
            emptyColumnSize={emptyColumnSize}
            isResizing={isResizing}
            scrollRef={scrollRef}
            showCellBorder={showCellBorder}
            onSetResizing={setResizing}
            onSetResizingColumn={setResizingColumn}
            onSetShowCellBorder={setShowCellBorder}
          />
          {items?.length > 0 ? (
            <TableBody {...bodyProps}>{items.map(renderRow)}</TableBody>
          ) : null}
        </div>
      </div>
    </>
  )
}

export default memo(TableComponent)
